import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Search } from "lucide-react";
import { Button, Input, Select } from "@/shared/rd";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { intlLocale } from "@/lib/formatting/locale";
import {
  dateFromIso,
  isoFromDate,
  rangeSummary,
} from "@/lib/formatting/date-range-labels";
import type {
  IntegrationLogDirection,
  IntegrationLogStatus,
} from "@/lib/api/types";
import type { IntegrationLogFiltersProps } from "../types/integration-logs.types";
import {
  buildIntegrationLogQuery,
  countActiveIntegrationLogFilters,
} from "../services/integration-log-search-params";

const STATUS_VALUES: IntegrationLogStatus[] = ["success", "failed", "pending"];
const DIRECTION_VALUES: IntegrationLogDirection[] = ["outbound", "inbound"];

const STATUS_LABEL_KEY: Record<IntegrationLogStatus, string> = {
  success: keys.integrationLogs.statusSuccess,
  failed: keys.integrationLogs.statusFailed,
  pending: keys.integrationLogs.statusPending,
};

const DIRECTION_LABEL_KEY: Record<IntegrationLogDirection, string> = {
  outbound: keys.integrationLogs.directionOutbound,
  inbound: keys.integrationLogs.directionInbound,
};

function isStatus(value: string): value is IntegrationLogStatus {
  return (STATUS_VALUES as string[]).includes(value);
}

function isDirection(value: string): value is IntegrationLogDirection {
  return (DIRECTION_VALUES as string[]).includes(value);
}

const APPLY_DEBOUNCE_MS = 300;

function normalize(value: string | undefined): string {
  return value?.trim() ?? "";
}

/**
 * Inline filter bar. Every control sits directly above the table — a support
 * agent chasing one order shouldn't have to open a dialog to narrow the list,
 * and the applied filters stay visible while reading results.
 */
export function IntegrationLogFilters({
  basePath,
  limit,
  locale,
  initialFilters,
  storeOptions,
}: IntegrationLogFiltersProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  // Future days are disabled — a log row can never be in the future.

  const [search, setSearch] = useState(normalize(initialFilters.search));
  const [storeId, setStoreId] = useState(normalize(initialFilters.storeId));
  const [status, setStatus] = useState(normalize(initialFilters.status));
  const [direction, setDirection] = useState(
    normalize(initialFilters.direction),
  );
  const [provider, setProvider] = useState(normalize(initialFilters.provider));
  const [dateFrom, setDateFrom] = useState(normalize(initialFilters.dateFrom));
  const [dateTo, setDateTo] = useState(normalize(initialFilters.dateTo));

  const activeFilterCount = countActiveIntegrationLogFilters(initialFilters);

  const nextSearch = normalize(search);
  const nextStoreId = normalize(storeId);
  const nextStatus = normalize(status);
  const nextDirection = normalize(direction);
  const nextProvider = normalize(provider);
  const nextDateFrom = normalize(dateFrom);
  const nextDateTo = normalize(dateTo);

  const hasPendingChanges =
    nextSearch !== normalize(initialFilters.search) ||
    nextStoreId !== normalize(initialFilters.storeId) ||
    nextStatus !== normalize(initialFilters.status) ||
    nextDirection !== normalize(initialFilters.direction) ||
    nextProvider !== normalize(initialFilters.provider) ||
    nextDateFrom !== normalize(initialFilters.dateFrom) ||
    nextDateTo !== normalize(initialFilters.dateTo);

  const dateLocale = intlLocale(locale);
  const dateRange = useMemo(
    () => ({ start: dateFromIso(nextDateFrom), end: dateFromIso(nextDateTo) }),
    [nextDateFrom, nextDateTo],
  );
  const dateSummary = rangeSummary(
    dateLocale,
    dateRange,
    t(keys.integrationLogs.filterDateAll),
  );

  const storeSelectOptions = useMemo(
    () => [
      { label: t(keys.integrationLogs.filterAllStores), value: "" },
      // Seller name rides along so two stores with the same name stay
      // distinguishable in the dropdown.
      ...storeOptions.map((store) => ({
        label: `${store.storeName} — ${store.sellerName}`,
        value: store.id,
      })),
    ],
    [storeOptions, t],
  );

  const statusOptions = useMemo(
    () => [
      { label: t(keys.integrationLogs.filterAllStatuses), value: "" },
      ...STATUS_VALUES.map((value) => ({
        label: t(STATUS_LABEL_KEY[value]),
        value,
      })),
    ],
    [t],
  );

  const directionOptions = useMemo(
    () => [
      { label: t(keys.integrationLogs.filterAllDirections), value: "" },
      ...DIRECTION_VALUES.map((value) => ({
        label: t(DIRECTION_LABEL_KEY[value]),
        value,
      })),
    ],
    [t],
  );

  // Filters apply themselves. One debounced effect covers every control, so
  // typing in the search box costs one request instead of one per keystroke,
  // and a dropdown change still feels instant. The guard is what stops this
  // from re-navigating to the URL we are already on (and looping): once the
  // navigation lands, the props it produced match the local state again.
  useEffect(() => {
    if (!hasPendingChanges) {
      return;
    }
    const timer = setTimeout(() => {
      navigate(
        `${basePath}?${buildIntegrationLogQuery({
          limit,
          offset: 0,
          filters: {
            search: nextSearch || undefined,
            storeId: nextStoreId || undefined,
            status: isStatus(nextStatus) ? nextStatus : undefined,
            direction: isDirection(nextDirection) ? nextDirection : undefined,
            provider: nextProvider || undefined,
            dateFrom: nextDateFrom || undefined,
            dateTo: nextDateTo || undefined,
          },
        })}`,
        // Replace, don't push: auto-applied filters would otherwise bury the
        // real previous page under a history entry per edit, and Back would
        // fight the local field state by restoring an older URL.
        { replace: true },
      );
    }, APPLY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    hasPendingChanges,
    nextSearch,
    nextStoreId,
    nextStatus,
    nextDirection,
    nextProvider,
    nextDateFrom,
    nextDateTo,
    basePath,
    limit,
    navigate,
  ]);

  // Resetting the fields is enough — the effect above navigates.
  function clearFilters() {
    setSearch("");
    setStoreId("");
    setStatus("");
    setDirection("");
    setProvider("");
    setDateFrom("");
    setDateTo("");
  }

  const today = isoFromDate(new Date());

  return (
    <form
      className="rd-toolbar"
      style={{ alignItems: "flex-end", gap: 10 }}
      onSubmit={(event) => event.preventDefault()}
    >
      <FilterField label={t(keys.integrationLogs.filterSearchLabel)} htmlFor="search">
        <Input
          id="search"
          name="search"
          type="search"
          size="sm"
          icon={Search}
          value={search}
          style={{ width: 240 }}
          placeholder={t(keys.integrationLogs.filterSearchPlaceholder)}
          onChange={(event) => setSearch(event.target.value)}
        />
      </FilterField>

      <FilterField label={t(keys.integrationLogs.filterStoreLabel)} htmlFor="storeId">
        <Select
          id="storeId"
          size="sm"
          value={storeId}
          style={{ width: 220 }}
          options={storeSelectOptions}
          onChange={(event) => setStoreId(event.target.value)}
        />
      </FilterField>

      <FilterField label={t(keys.integrationLogs.filterStatusLabel)} htmlFor="status">
        <Select
          id="status"
          size="sm"
          value={status}
          style={{ width: 150 }}
          options={statusOptions}
          onChange={(event) => setStatus(event.target.value)}
        />
      </FilterField>

      <FilterField label={t(keys.integrationLogs.filterDirectionLabel)} htmlFor="direction">
        <Select
          id="direction"
          size="sm"
          value={direction}
          style={{ width: 150 }}
          options={directionOptions}
          onChange={(event) => setDirection(event.target.value)}
        />
      </FilterField>

      <FilterField label={t(keys.integrationLogs.filterProviderLabel)} htmlFor="provider">
        <Input
          id="provider"
          name="provider"
          size="sm"
          value={provider}
          dir="ltr"
          style={{ width: 140 }}
          placeholder={t(keys.integrationLogs.filterProviderPlaceholder)}
          onChange={(event) => setProvider(event.target.value)}
        />
      </FilterField>

      <fieldset
        style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}
        title={dateSummary}
      >
        <legend style={labelStyle}>{t(keys.integrationLogs.filterDateRangeLabel)}</legend>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Input
            type="date"
            size="sm"
            aria-label={t("integrationLogs.filterDateFrom")}
            value={dateFrom}
            max={dateTo || today}
            style={{ width: 150 }}
            onChange={(event) => setDateFrom(event.target.value)}
          />
          <span aria-hidden="true" style={{ color: "var(--rd-text-faint)" }}>
            –
          </span>
          <Input
            type="date"
            size="sm"
            aria-label={t("integrationLogs.filterDateTo")}
            value={dateTo}
            min={dateFrom || undefined}
            max={today}
            style={{ width: 150 }}
            onChange={(event) => setDateTo(event.target.value)}
          />
        </span>
      </fieldset>

      {activeFilterCount > 0 ? (
        <Button type="button" variant="ghost" size="sm" muted onClick={clearFilters}>
          {t(keys.common.clearFilters)}
        </Button>
      ) : null}
    </form>
  );
}

const labelStyle = {
  padding: 0,
  fontSize: 12,
  color: "var(--rd-text-secondary)",
} as const;

function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <label htmlFor={htmlFor} style={labelStyle}>
        {label}
      </label>
      {children}
    </div>
  );
}
